/**
 * API Client
 * 
 * Centralized API client with enhanced error handling and request/response interceptors.
 * This replaces direct axios usage throughout the app for better consistency.
 */

import axios from 'axios';
import toast from 'react-hot-toast';
import { processPendingMutations } from '../serviceWorkerRegistration';

// Base URL - can be configured via environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://stockpro-api.khalfaouimanar28.workers.dev/api';

// Create an axios instance with custom config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

// Track if we're currently offline
let isOffline = !navigator.onLine;

// Update offline status
window.addEventListener('online', () => {
  isOffline = false;
  // Just log that we're back online
  console.log('Back online - no offline mutations to process');
});

window.addEventListener('offline', () => {
  isOffline = true;
});

// Request interceptor
apiClient.interceptors.request.use(
  config => {
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add timestamp to prevent caching issues
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _ts: new Date().getTime()
      };
    }
    
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  response => {
    // Handle successful responses
    return response;
  },
  error => {
    // Handle network errors
    if (!error.response) {
      if (!navigator.onLine || isOffline) {
        // We're offline - inform the user
        console.warn('Network error while offline.');
        toast.error('You are offline. Please reconnect to make changes.', { id: 'offline-error' });
        return Promise.reject({
          isOffline: true,
          message: 'You are offline. Please reconnect to continue.'
        });
      } else {
        // Network error but we think we're online
        console.error('Network error but browser reports online status:', error);
        toast.error('Cannot reach server. Please check your connection.', { id: 'network-error' });
        
        // Mark as offline
        isOffline = true;
        
        // Dispatch an offline event to update UI
        window.dispatchEvent(new Event('offline'));
      }
    }
    // Handle HTTP errors
    else {
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          toast.error(data?.message || 'Invalid request');
          break;
        case 401:
          toast.error('Your session has expired. Please log in again.');
          // Trigger logout or redirect to login
          window.dispatchEvent(new CustomEvent('auth-error', { detail: { status, message: 'Unauthorized' } }));
          break;
        case 403:
          toast.error('You do not have permission to perform this action');
          break;
        case 404:
          // Don't show toast for 404s - handle at component level
          console.warn('Resource not found:', error.config.url);
          break;
        case 409:
          // Conflict error - usually indicates another client has modified the data
          toast.error('This data has been modified elsewhere. Please refresh and try again.');
          break;
        case 422:
          // Validation error
          const validationErrors = data?.errors || {};
          const firstError = Object.values(validationErrors)[0];
          toast.error(firstError || 'Validation error');
          break;
        case 429:
          toast.error('Too many requests. Please try again later.');
          break;
        case 500:
          toast.error('Server error. Please try again later.');
          break;
        default:
          toast.error('An error occurred. Please try again.');
      }
    }
    
    return Promise.reject(error);
  }
);

// API wrapper functions with enhanced error handling
const api = {
  get: async (url, config = {}) => {
    try {
      const response = await apiClient.get(url, config);
      return response.data;
    } catch (error) {
      if (error.isOffline) {
        throw error;
      }
      console.error(`GET ${url} failed:`, error);
      throw error;
    }
  },
  
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.post(url, data, config);
      return response.data;
    } catch (error) {
      if (error.isOffline) {
        throw error;
      }
      console.error(`POST ${url} failed:`, error);
      throw error;
    }
  },
  
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error) {
      if (error.isOffline) {
        throw error;
      }
      console.error(`PUT ${url} failed:`, error);
      throw error;
    }
  },
  
  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.patch(url, data, config);
      return response.data;
    } catch (error) {
      if (error.isOffline) {
        throw error;
      }
      console.error(`PATCH ${url} failed:`, error);
      throw error;
    }
  },
  
  delete: async (url, config = {}) => {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (error) {
      if (error.isOffline) {
        throw error;
      }
      console.error(`DELETE ${url} failed:`, error);
      throw error;
    }
  },
  
  // Check server health - useful for connectivity tests
  checkHealth: async () => {
    try {
      const response = await apiClient.get('/health');
      return response.data && response.data.status === 'OK';
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  },
  
  // Get instance - only use directly if absolutely necessary
  getInstance: () => apiClient
};

export default api;